import { ChronoUnit, convert, DayOfWeek, Instant, LocalDateTime, ZonedDateTime, ZoneId } from "@js-joda/core";
import { DweenTimeUnit, DweenTimeUnitEnum, toDate, toZonedDateTime } from "./time-utils";
import { toChronoUnit } from "../components/fragments/dashboard/utils";

export namespace JodaTimeUtils {
  const validHandledUnitsInFunctions = [
    ChronoUnit.MILLIS,
    ChronoUnit.SECONDS,
    ChronoUnit.MINUTES,
    ChronoUnit.HOURS,
    ChronoUnit.DAYS,
    ChronoUnit.WEEKS,
    ChronoUnit.MONTHS,
    ChronoUnit.YEARS,
  ];

  const toDweenUnit = (unit: any): DweenTimeUnitEnum => {
    if (typeof unit === "object") {
      const { _name: name } = unit;
      return DweenTimeUnitEnum[name?.toUpperCase() as keyof typeof DweenTimeUnitEnum] || null;
    }
    const resolvedUnit = DweenTimeUnitEnum[unit?.toString().toUpperCase() as keyof typeof DweenTimeUnitEnum];
    return resolvedUnit || null;
  };

  const validDweenTimeUnit = (unit: any): unit is DweenTimeUnitEnum => {
    return Object.values(DweenTimeUnitEnum).includes(unit);
  };

  export const truncateToZoned = (
    date: ZonedDateTime | Date | string,
    unit: ChronoUnit | DweenTimeUnit,
    zoneId: ZoneId
  ): ZonedDateTime => {
    unit = toDweenUnit(unit);
    if (!validDweenTimeUnit(unit)) {
      throw new Error("Unhandled time unit.");
    }

    const tempDate = toZonedDateTime(date, zoneId);

    switch (unit) {
      case DweenTimeUnitEnum.MILLIS:
      case DweenTimeUnitEnum.SECONDS:
      case DweenTimeUnitEnum.MINUTES:
      case DweenTimeUnitEnum.HOURS:
      case DweenTimeUnitEnum.DAYS:
        return tempDate.truncatedTo(toChronoUnit(unit));
      case DweenTimeUnitEnum.WEEKS:
        return tempDate.truncatedTo(ChronoUnit.DAYS).with(DayOfWeek.MONDAY);
      case DweenTimeUnitEnum.MONTHS:
        return tempDate.truncatedTo(ChronoUnit.DAYS).withDayOfMonth(1);
      case DweenTimeUnitEnum.YEARS:
        return tempDate.truncatedTo(ChronoUnit.DAYS).withDayOfYear(1);
      default:
        throw new Error("Unhandled time unit.");
    }
  };

  export const truncateTo = (date: ZonedDateTime | Date | string, unit: ChronoUnit | DweenTimeUnit, zoneId: ZoneId): Date =>
    toDate(truncateToZoned(date, unit, zoneId));

  export const CeilingTo = (date: ZonedDateTime | Date | string, unit: ChronoUnit, zoneId: ZoneId): Date => {
    if (!validHandledUnitsInFunctions.includes(unit)) {
      throw new Error("Unhandled time unit.");
    }

    const tempDate = toZonedDateTime(date, zoneId);
    const truncatedDate = truncateToZoned(tempDate, unit, zoneId);

    const mapFn = (u: ChronoUnit): ZonedDateTime => {
      switch (u) {
        case ChronoUnit.MILLIS:
        case ChronoUnit.SECONDS:
        case ChronoUnit.MINUTES:
        case ChronoUnit.HOURS:
        case ChronoUnit.DAYS:
        case ChronoUnit.WEEKS:
        case ChronoUnit.MONTHS:
        case ChronoUnit.YEARS:
          if (truncatedDate.isBefore(tempDate)) {
            return truncatedDate.plus(1, unit);
          }
          return truncatedDate;
        default:
          throw new Error("Unhandled time unit.");
      }
    };

    return toDate(mapFn(unit));
  };

  export const truncateAndOffsetLocalDatetime = (
    date: LocalDateTime,
    truncateToUnit: DweenTimeUnit,
    offset: number,
    offsetUnit: DweenTimeUnit
  ): LocalDateTime => {
    const truncatedDate = date.truncatedTo(toChronoUnit(truncateToUnit));
    return truncatedDate.plus(offset, toChronoUnit(offsetUnit));
  };
}

export const getStartOfYesterday = (zoneId: ZoneId): Date => {
  return convert(Instant.now().atZone(zoneId).truncatedTo(ChronoUnit.DAYS).minusDays(1)).toDate();
};

export const getEndOfYesterday = (zoneId: ZoneId): Date => {
  return convert(Instant.now().atZone(zoneId).truncatedTo(ChronoUnit.DAYS).minusSeconds(1)).toDate();
};
